import { CommonModule } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { ID, ID_PARAM, PATH_SEPARATOR } from "@shared/constants/route-variables.constants";
import { CONTRACTDETAILS, PAGE_NON_TROUVEE } from "@shared/constants/route.constants";
import {
    CATEGORY_MANAGEMENT_TITLE_TRANSLATE_KEY,
    DEFAULT_TITLE_TRANSLATE_KEY,
    FILE_EDITION_TITLE_TRANSLATE_KEY,
    STORED_DOCUMENTS_VIEWCHILD_UPLOAD,
} from "@shared/constants/stored-documents.constants";
import { convertBase64ToBlob } from "@shared/helpers/file.helper";
import { StoredDocumentCategory } from "@shared/models/stored-document-category.model";
import { StoredDocumentCreationForm } from "@shared/models/stored-document-creation-form.model";
import { StoredDocumentEditionForm } from "@shared/models/stored-document-edition-form.model";
import { StoredDocumentModifyCategoriesForm } from "@shared/models/stored-document-modify-categories-form.model";
import { StoredDocument } from "@shared/models/stored-document.model";
import { StoredDocumentsInformations } from "@shared/models/stored-documents-informations.model";
import { StoredDocumentsService } from "@shared/services/stored-documents.service";
import { ButtonModule } from "primeng/button";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { SkeletonModule } from "primeng/skeleton";
import { BehaviorSubject, map, Subject, takeUntil } from "rxjs";
import { StoredDocumentsCategoryManagementComponent } from "./stored-documents-category-management/stored-documents-category-management.component";
import { StoredDocumentsFileEditionComponent } from "./stored-documents-file-edition/stored-documents-file-edition.component";
import { StoredDocumentsListComponent } from "./stored-documents-list/stored-documents-list.component";
import { StoredDocumentsMode } from "./stored-documents-mode.enum";
import { StoredDocumentsUploadComponent } from "./stored-documents-upload/stored-documents-upload.component";

@Component({
  selector: "app-stored-documents",
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    OverlayPanelModule,
    RouterModule,
    SkeletonModule,
    StoredDocumentsCategoryManagementComponent,
    StoredDocumentsFileEditionComponent,
    StoredDocumentsListComponent,
    StoredDocumentsUploadComponent,
    StoredDocumentsCategoryManagementComponent,
    TranslateModule,
  ],
  templateUrl: "./stored-documents.component.html",
})
export class StoredDocumentsComponent implements OnInit, OnDestroy {
  @ViewChild(STORED_DOCUMENTS_VIEWCHILD_UPLOAD)
  documentUpload!: StoredDocumentsUploadComponent;
  policyId!: string;

  documentsInformations$ = new BehaviorSubject<StoredDocumentsInformations | undefined>(undefined);
  documentsCategories$ = new BehaviorSubject<StoredDocumentCategory[] | undefined>(undefined);

  EnumStoredDocumentsMode = StoredDocumentsMode; // Expose the enum to the template
  mode: StoredDocumentsMode;
  lastLoadedCategories: StoredDocumentCategory[] = [];

  editedDocument: StoredDocument | undefined;

  readonly unsubscribe$ = new Subject<void>();

  constructor(
    private storedDocumentsService: StoredDocumentsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router
  ) {
    this.mode = StoredDocumentsMode.None;
  }

  ngOnInit() {
    this.policyId = this.route.snapshot.params[ID];

    this.loadDocumentsInformations();
    this.loadDocumentsCategories();
  }

  ngOnDestroy() {
    // Unsubscribe all
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  get contractDetailsPath(): string {
    return PATH_SEPARATOR + CONTRACTDETAILS.replace(ID_PARAM, this.policyId);
  }

  get titleTranslationKey() {
    switch (this.mode) {
      case StoredDocumentsMode.CategoryManagement:
        return CATEGORY_MANAGEMENT_TITLE_TRANSLATE_KEY;
      case StoredDocumentsMode.FileEdition:
        return FILE_EDITION_TITLE_TRANSLATE_KEY;
      default:
        return DEFAULT_TITLE_TRANSLATE_KEY;
    }
  }

  loadDocumentsInformations() {
    this.storedDocumentsService
      .getStoredDocumentsInformations(this.policyId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: data => this.documentsInformations$.next(data), // Update the subject (avoid the brief blank state between loads)
        error: (err: HttpErrorResponse) => {
          // Redirect if not found or forbidden
          if ([403, 404].includes(err.status)) this.router.navigate([PAGE_NON_TROUVEE]);
        },
      });
  }

  loadDocumentsCategories() {
    this.storedDocumentsService
      .getStoredDocumentsCategories(this.policyId)
      .pipe(
        map(d => d),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(data => {
        this.documentsCategories$.next(data); // Update the subject (avoid the brief blank state between loads)
        this.lastLoadedCategories = data;
      });
  }

  closeMode() {
    this.mode = StoredDocumentsMode.None;
  }

  onDownloadDocument(storedDocument: StoredDocument) {
    this.storedDocumentsService
      .getStoredDocumentContent(this.policyId, storedDocument.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        const blob = convertBase64ToBlob(atob(data.fileContents));
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = data.fileDownloadName;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  onEditDocument(storedDocument: StoredDocument) {
    this.mode = StoredDocumentsMode.FileEdition;
    this.editedDocument = storedDocument;
  }

  onEditionValidated(edition: StoredDocumentEditionForm) {
    this.storedDocumentsService
      .editStoredDocument(this.policyId, edition)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: () => {
          this.mode = StoredDocumentsMode.None;
          this.loadDocumentsInformations();
          this.loadDocumentsCategories();
        },
      });
  }

  onEditionCancelled() {
    this.mode = StoredDocumentsMode.None;
  }

  onDeleteDocument(storedDocument: StoredDocument) {
    this.storedDocumentsService
      .deleteStoredDocument(this.policyId, storedDocument.id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.loadDocumentsInformations());
  }

  editListEvent(event: boolean) {
    if (event) this.mode = StoredDocumentsMode.CategoryManagement;
  }

  newCategoryEvent(event: StoredDocumentCategory[]) {
    let addedCategoryNames: string[] = [];
    let removedCategoryIds: number[] = [];
    let renamedCategory: { categoryId: number; newName: string }[] = [];
    removedCategoryIds = this.lastLoadedCategories
      .filter(oldItem => !event.some(newItem => oldItem.name === newItem.name))
      .map(item => item.id);
    addedCategoryNames = event
      .filter(newItem => !this.lastLoadedCategories.some(oldItem => newItem.name === oldItem.name))
      .map(item => item.name);
    renamedCategory = event
      .filter(newItem => {
        const oldItem = this.lastLoadedCategories.find(item => item.id === newItem.id);
        return oldItem && oldItem.name !== newItem.name;
      })
      .map(item => ({ categoryId: item.id, newName: item.name }));
    const form: StoredDocumentModifyCategoriesForm = {
      create: addedCategoryNames,
      remove: removedCategoryIds,
      rename: renamedCategory,
    };
    this.storedDocumentsService
      .modifyStoredDocumentsCategories(this.policyId, form)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: data => {
          this.documentsCategories$.next(data); // Update the subject (avoid the brief blank state between loads)
          this.lastLoadedCategories = data;
          this.mode = StoredDocumentsMode.None;
        },
      });
  }

  cancelNewCategoryEvent(event: boolean) {
    if (event) this.mode = StoredDocumentsMode.None;
  }

  onValidateUploadFile(documentForm: StoredDocumentCreationForm) {
    this.storedDocumentsService.createStoredDocument(this.policyId, documentForm).subscribe({
      next: () => {
        this.loadDocumentsCategories();
        this.loadDocumentsInformations();
        this.documentUpload.clear();
      },
    });
  }
}
